import Swal, { type SweetAlertIcon } from 'sweetalert2';
// import '@sweetalert2/theme-bulma/bulma.css';

export interface CustomAlertParams {
    /** Mensaje de texto */
    mensaje?: string | null;
    /** Tiempo (segundos) en el que el mensaje será visible antes de cerrarse */
    tiempo?: number;
    /** Si `true` el botón cancelar será visible (predeterminado: `false`) */
    botonCancelar?: boolean | null;
    /** Si `true` el loader será visible (predeterminado `false`) */
    loading?: boolean | null;
    /** Título de la alerta */
    titulo?: string | null;
    /** Texto del botón aceptar */
    textoBotonAceptar?: string | null;
    /** Color bg del botón aceptar */
    colorBotonAceptar?: string | null;
    /** Texto del botón cancelar */
    textoBotonCancelar?: string | null;
    /** Color bg del botón cancelar */
    colorBotonCancelar?: string | null;
    /** Icono de la alerta (predeterminado: `success`) */
    icon?: SweetAlertIcon;
    /** Tamaño del icono en píxeles (predeterminado: toast = 32px, modal = 45px) */
    iconSize?: number;
    /** Función callback */
    callback?: ((isConfirmed: boolean) => void) | null;
    /** Si `true` cierra el modal al hacer clic fuera del modal */
    cerrarClicFuera?: boolean;
    /** Si `true` cierra el modal al pulsar la tecla escape */
    cerrarEsc?: boolean;
    /** Si `true` muestra un toast en lugar de un modal */
    toast?: boolean;
    /** Theme personalizado para el toast o modal */
    theme?: 'light' | 'dark' | 'colored';
    posicionToast?: 'top' | 'top-start' | 'top-end' | 'center' | 'center-start' | 'center-end' | 'bottom' | 'bottom-start' | 'bottom-end';
    /** Mensaje de tiempo para mostrar en la alerta */
    mensajeTiempoSWL?: string;
}

/**
 * @description Muestra una alerta personalizada usando SweetAlert2.
 * Si loading = `true` el botón aceptar no será visible, en su lugar se mostrará el loading.
 * Si toast = `true` muestra un toast en lugar de un modal.
 */
export function customAlert(params: CustomAlertParams): void {
    const {
        mensaje = '',
        botonCancelar = false,
        loading = false,
        icon = 'success',
        iconSize = params.toast ? 32 : 45,
        callback = null,
        titulo = null,
        textoBotonAceptar = 'Aceptar',
        colorBotonAceptar = '#0050dd',
        textoBotonCancelar = 'Cancelar',
        colorBotonCancelar = '#ef3340',
        cerrarClicFuera = true,
        cerrarEsc = true,
        tiempo,
        toast = false,
        theme = 'light',
        posicionToast = 'top-end',
        mensajeTiempoSWL = 'Este mensaje se ocultará en'
    } = params;

    const clic = cerrarClicFuera === true;
    const esc = cerrarEsc === true;

    // Configuración para toast
    if (toast) {
        const Toast = Swal.mixin({
            toast: true,
            position: posicionToast,
            showConfirmButton: false,
            timer: tiempo && tiempo > 0 ? tiempo * 1000 : 4000,
            timerProgressBar: true,
            didOpen: (toastElement) => {
                toastElement.addEventListener('mouseenter', Swal.stopTimer);
                toastElement.addEventListener('mouseleave', Swal.resumeTimer);
                
                // Aplicar tamaño del icono si se especificó
                if (iconSize) {
                    // const iconElement = toastElement.querySelector('.swal2-icon');
                    // if (iconElement instanceof HTMLElement) {
                    //     iconElement.style.width = `${iconSize}px`;
                    //     iconElement.style.height = `${iconSize}px`;
                    //     iconElement.style.minWidth = `${iconSize}px`;
                    //     iconElement.style.minHeight = `${iconSize}px`;
                    //     iconElement.style.borderWidth = `${Math.max(2, iconSize * 0.04)}px`;
                        
                    //     // Ajustar el contenido interno del icono
                    //     const iconContent = iconElement.querySelector('.swal2-icon-content');
                    //     if (iconContent instanceof HTMLElement) {
                    //         iconContent.style.fontSize = `${iconSize * 0.75}px`;
                    //     }
                        
                    //     // Ajustar líneas del icono de error
                    //     const xMark = iconElement.querySelectorAll('.swal2-x-mark-line-left, .swal2-x-mark-line-right');
                    //     xMark.forEach((line) => {
                    //         if (line instanceof HTMLElement) {
                    //             line.style.width = `${iconSize * 0.375}px`;
                    //             line.style.height = `${Math.max(2, iconSize * 0.06)}px`;
                    //         }
                    //     });
                    // }
                }
            },
            customClass: {
                popup: getToastThemeClass(theme),
                title: 'text-sm font-medium',
                container: '!z-[1400]',
                timerProgressBar: getTimerProgressBarClass(theme)
            },
            heightAuto: false,
            backdrop: false
        });

        Toast.fire({
            icon: icon as SweetAlertIcon,
            title: (titulo ?? mensaje) || undefined
        }).then(() => {
            if (callback) callback(true);
        });

        return;
    }

    // Configuración para modal normal
    const contenedorTiempo = `${tiempo && tiempo > 0 ? `<span id="mensajeTiempoSWL">${mensajeTiempoSWL} <b></b> segundos.</span>` : ''}`;

    Swal.fire({
        icon,
        title: titulo || undefined,
        html: mensaje
            ? `<div>
                ${mensaje}
                <br/>
                ${contenedorTiempo}
            </div>`
            : '',
        timer: tiempo && tiempo > 0 ? tiempo * 1000 : undefined,
        timerProgressBar: false,
        showCancelButton: botonCancelar || false,
        confirmButtonText: textoBotonAceptar || 'Aceptar',
        cancelButtonText: textoBotonCancelar || 'Cancelar',
        confirmButtonColor: colorBotonAceptar || '#0050dd',
        cancelButtonColor: colorBotonCancelar || '#ef3340',
        customClass: {
            confirmButton: '!rounded-r-xl !rounded-l-xl !py-2',
            cancelButton: '!rounded-r-xl !rounded-l-xl !py-2',
            popup: getModalThemeClass(theme),
            container: '!z-[1400]'
        },
        heightAuto: false,
        allowOutsideClick: clic,
        allowEscapeKey: esc,
        didOpen: () => {
            if (loading) Swal.showLoading();
            
            // Aplicar tamaño del icono si se especificó
            if (iconSize) {
                // const iconElement = Swal.getIcon();
                // if (iconElement) {
                //     iconElement.style.width = `${iconSize}px`;
                //     iconElement.style.height = `${iconSize}px`;
                //     iconElement.style.minWidth = `${iconSize}px`;
                //     iconElement.style.minHeight = `${iconSize}px`;
                //     iconElement.style.borderWidth = `${Math.max(2, iconSize * 0.04)}px`;
                    
                //     // Ajustar el contenido interno del icono (X, ✓, ?, i, etc.)
                //     const iconContent = iconElement.querySelector('.swal2-icon-content');
                //     if (iconContent instanceof HTMLElement) {
                //         iconContent.style.fontSize = `${iconSize * 0.75}px`;
                //     }
                    
                //     // Ajustar líneas del icono de error
                //     const xMark = iconElement.querySelectorAll('.swal2-x-mark-line-left, .swal2-x-mark-line-right');
                //     xMark.forEach((line) => {
                //         if (line instanceof HTMLElement) {
                //             line.style.width = `${iconSize * 0.375}px`;
                //             line.style.height = `${Math.max(2, iconSize * 0.06)}px`;
                //         }
                //     });
                // }
            }

            if (tiempo && tiempo > 0) {
                const timerInterval = setInterval(() => {
                    const b = Swal.getHtmlContainer()?.querySelector('b');
                    if (b) {
                        const seg = Math.ceil((Swal.getTimerLeft() || 0) / 1000);
                        b.textContent = String(seg);
                    }
                }, 100);
                Swal.getHtmlContainer()?.setAttribute('data-timer-interval', String(timerInterval));
            }
        },
        willClose: () => {
            if (tiempo && tiempo > 0) {
                const timerInterval = Swal.getHtmlContainer()?.getAttribute('data-timer-interval');
                if (timerInterval) {
                    clearInterval(Number(timerInterval));
                    if (callback) callback(true);
                }
            }
        }
    }).then((result) => {
        if (callback) callback(result.isConfirmed);
    });
}

/**
 * Obtiene las clases de tema para toast
 */
function getToastThemeClass(theme: 'light' | 'dark' | 'colored'): string {
    switch (theme) {
        case 'dark':
            return '!bg-gray-800 !text-white';
        case 'colored':
            return '!bg-gradient-to-r !from-blue-500 !to-blue-600 !text-white';
        case 'light':
        default:
            return '!bg-white !text-gray-800';
    }
}

/**
 * Obtiene las clases de tema para modal
 */
function getModalThemeClass(theme: 'light' | 'dark' | 'colored'): string {
    switch (theme) {
        case 'dark':
            return '!bg-gray-800 !text-white';
        case 'colored':
            return '!bg-gradient-to-br !from-blue-50 !to-indigo-50';
        case 'light':
        default:
            return '';
    }
}

/**
 * Obtiene las clases para la barra de progreso del timer según el tema
 */
function getTimerProgressBarClass(theme: 'light' | 'dark' | 'colored'): string {
    switch (theme) {
        case 'dark':
            return '!bg-blue-400'; // Color claro para contraste en fondo oscuro
        case 'colored':
            return '!bg-white !opacity-80';
        case 'light':
        default:
            return '!bg-blue-600'; // Color oscuro para contraste en fondo claro
    }
}

export default customAlert;
